import { CoberturaAtendimento } from '@/components/bko/CoberturaAtendimento';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { FAIXA_BKO_LABEL, NotaBkoResultado } from '@/hooks/useNotaBkoMes';
import { Lightbulb, BookOpen, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import { GuiaNotasDialog } from './GuiaNotasDialog';
import { corrigirTextoNota, funcaoCurta } from '@/lib/notaPrimeTexto';
import { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { NotaPrimeDialogHeader } from './NotaPrimeDialogHeader';
import { NotaPrimeMesControle } from './NotaPrimeMesControle';

// ============================================================================
// NOTA PRIME — os 10 pilares de uma pessoa (43b, 21/08/2026). Zero mudança de
// cálculo: pesos, pontos e normalização vêm prontos da RPC. A leitura do topo
// deixou de ser frase genérica — ela NOMEIA o pilar de maior ganho, que é a
// única informação acionável do modal. Texto do banco passa por
// corrigirTextoNota (acento + número brasileiro) só na exibição.
// ============================================================================

const GRID_PILARES = 'grid items-center gap-3 [grid-template-columns:minmax(0,0.82fr)_minmax(0,1.3fr)_80px_54px]';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nota: NotaBkoResultado;
  /** Quando true, mostra a sugestão de cada pilar (página /minha-performance). */
  mostrarSugestoes?: boolean;
  /** Veio da lista da Nota Prime — o breadcrumb "‹ Nota Prime" volta pra ela. */
  onVoltar?: () => void;
  /** Setas ‹ › — navegam entre as pessoas da lista sem voltar. */
  onAnterior?: () => void;
  onProxima?: () => void;
  /** A régua da próxima pessoa ainda está chegando (a anterior segura a tela). */
  carregando?: boolean;
  competencia?: Date;
  onCompetenciaChange?: (competencia: Date) => void;
}

export function NotaBkoDetalheModal({
  open, onOpenChange, nota, mostrarSugestoes, onVoltar, onAnterior, onProxima, carregando,
  competencia, onCompetenciaChange,
}: Props) {
  const [guiaAberto, setGuiaAberto] = useState(false);
  const pilaresAplicaveis = nota.breakdown.filter(p => p.aplicavel !== false && p.pontos != null);
  const totalPeso = pilaresAplicaveis.reduce((s, p) => s + p.peso, 0);

  // A leitura acionável: quantos pontos estão em aberto e onde mora o maior deles.
  const emAberto = pilaresAplicaveis.reduce((s, p) => s + Math.max(0, p.peso - (p.pontos ?? 0)), 0);
  const maior = pilaresAplicaveis.reduce(
    (m, p) => (p.peso - (p.pontos ?? 0) > (m ? m.peso - (m.pontos ?? 0) : 0) ? p : m),
    null as NotaBkoResultado['breakdown'][number] | null,
  );
  const maiorGap = maior ? maior.peso - (maior.pontos ?? 0) : 0;

  const competenciaExibida = competencia ?? new Date(`${nota.competencia.slice(0, 10)}T12:00:00`);
  const rotuloCompetencia = format(competenciaExibida, 'MMMM yyyy', { locale: ptBR });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(790px,88vh)] w-[772px] max-w-[calc(100vw-32px)] flex-col gap-0 overflow-hidden rounded-2xl border-border bg-card p-0 shadow-2xl [&>button.absolute]:hidden">
        <NotaPrimeDialogHeader
          titulo={(
            <span className="flex min-w-0 items-center gap-2">
            {onVoltar ? (
              <>
                <button
                  type="button"
                  onClick={onVoltar}
                  className="flex flex-shrink-0 items-center gap-1 text-[11.5px] font-semibold text-white/70 transition-colors hover:text-white"
                >
                  <ChevronLeft className="h-3 w-3" /> Nota Prime
                </button>
                <span className="flex-shrink-0 text-white/40">›</span>
              </>
            ) : (
              <span className="flex-shrink-0 text-[13px] font-bold text-white/70">Nota Prime —</span>
            )}
            <span className="truncate">{nota.bko_nome}</span>
            <span className="flex-shrink-0 rounded-md bg-white/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white/70">
              {funcaoCurta(nota.bko_funcao)}
            </span>
            </span>
          )}
          descricao={`${nota.breakdown.length} pilares da régua do cargo em ${rotuloCompetencia}. Total possível ${totalPeso} pontos${totalPeso !== 100 ? ' (normalizado para 100)' : ''}.`}
          onClose={() => onOpenChange(false)}
          controles={onCompetenciaChange
            ? <NotaPrimeMesControle value={competenciaExibida} onChange={onCompetenciaChange} inverted />
            : <span className="text-[11px] font-bold capitalize text-white/80">{rotuloCompetencia}</span>}
        />
        <CoberturaAtendimento nota={nota} />

        {/* resumo fixo — a nota e a leitura acionável */}
        <div className={cn('flex flex-shrink-0 items-center gap-5 border-b border-border px-5 py-3 transition-opacity', carregando && 'opacity-60')}>
          <div className="flex-shrink-0">
            <div className="text-[9px] font-bold uppercase tracking-[1.1px] text-muted-label">Nota do mês</div>
            <div className="flex items-baseline gap-1.5">
              <span className="font-display text-[34px] font-bold leading-none tracking-[-1.2px] tabular-nums text-primary">
                {nota.nota}
              </span>
              <span className="text-[11px] text-muted-foreground/60">de 100</span>
            </div>
            <div className="mt-0.5 text-[10.5px] font-bold text-muted-foreground">{FAIXA_BKO_LABEL[nota.faixa]}</div>
          </div>
          <p className="min-w-0 flex-1 text-[12px] leading-[1.55] text-muted-foreground">
            {corrigirTextoNota(nota.diagnostico)}{' '}
            {emAberto === 0 ? (
              <>Nenhum ponto em aberto — os {nota.breakdown.length} pilares estão cheios.</>
            ) : maiorGap === emAberto ? (
              <>
                <b className="font-bold text-card-foreground">{emAberto} {emAberto === 1 ? 'ponto' : 'pontos'}</b> em aberto,
                todos num pilar só: {corrigirTextoNota(maior?.rotulo ?? maior?.pilar)}.
              </>
            ) : (
              <>
                <b className="font-bold text-card-foreground">{emAberto} pontos</b> em aberto, e{' '}
                <b className="font-bold text-card-foreground">{maiorGap} deles</b> estão no pilar de maior ganho:{' '}
                {corrigirTextoNota(maior?.rotulo ?? maior?.pilar)}.
              </>
            )}
          </p>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={() => setGuiaAberto(true)}
              className="flex h-7 items-center gap-1.5 rounded-lg border border-border bg-surface px-2.5 text-[11px] font-semibold text-card-foreground transition-colors hover:border-primary/40 hover:text-primary"
            >
              <BookOpen className="h-3 w-3" /> Guia da nota
            </button>
            {(onAnterior || onProxima) && (
              <>
                <button
                  type="button"
                  onClick={onAnterior}
                  disabled={!onAnterior}
                  title="Pessoa anterior"
                  className="grid h-7 w-7 place-items-center rounded-lg border border-border bg-surface text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-default disabled:opacity-35 disabled:hover:border-border disabled:hover:text-muted-foreground"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={onProxima}
                  disabled={!onProxima}
                  title="Próxima pessoa"
                  className="grid h-7 w-7 place-items-center rounded-lg border border-border bg-surface text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary disabled:cursor-default disabled:opacity-35 disabled:hover:border-border disabled:hover:text-muted-foreground"
                >
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </>
            )}
          </div>
        </div>

        {/* header da tabela */}
        <div className={cn(GRID_PILARES, 'h-[26px] flex-shrink-0 border-b border-border px-5')}>
          <div className="text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-label">Pilar</div>
          <div className="text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-label">Como está</div>
          <div className="text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-label">Progresso</div>
          <div className="text-right text-[8.5px] font-bold uppercase tracking-[1.1px] text-muted-label">Pontos</div>
        </div>

        {/* só os pilares rolam */}
        <div className={cn('scrollbar-hide min-h-0 flex-1 overflow-y-auto transition-opacity', carregando && 'opacity-60')}>
          {nota.breakdown.map((p, idx) => {
            const aplicavel = p.aplicavel !== false && p.pontos != null;
            const pontos = p.pontos ?? 0;
            const pct = aplicavel && p.peso > 0 ? Math.min(100, (pontos / p.peso) * 100) : 0;
            const cheio = aplicavel && p.peso > 0 && pontos >= p.peso;
            const zerado = aplicavel && p.peso > 0 && pontos === 0;
            const fraco = aplicavel && p.peso > 0 && pontos / p.peso < 0.4;
            const comDica = mostrarSugestoes && fraco && p.sugestao;
            return (
              <div key={p.pilar + idx} className={cn(idx % 2 !== 0 && 'bg-muted/20', 'border-b border-border')}>
                <div className={cn(GRID_PILARES, 'h-8 px-5')}>
                  <span className={cn('truncate text-[12px]', zerado ? 'font-bold text-primary' : 'font-semibold text-card-foreground')} title={corrigirTextoNota(p.rotulo ?? p.pilar)}>
                    {corrigirTextoNota(p.rotulo ?? p.pilar)}
                  </span>
                  <span className="truncate text-[11px] text-muted-foreground" title={corrigirTextoNota(p.detalhe)}>
                    {corrigirTextoNota(p.detalhe)}
                  </span>
                  <span className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                    <span
                      className={cn('block h-full rounded-full',
                        cheio ? 'bg-primary/70' : 'bg-primary')}
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="text-right text-[12px] tabular-nums">
                    <b className={cn('font-display font-bold', zerado ? 'text-primary' : 'text-card-foreground')}>{aplicavel ? p.pontos : 'N/A'}</b>
                    {aplicavel && <span className="text-muted-foreground/50"> / {p.peso}</span>}
                  </span>
                </div>
                {comDica && (
                  <div className="flex items-start gap-1.5 px-5 pb-2 pl-5">
                    <Lightbulb className="mt-[1px] h-[11px] w-[11px] flex-shrink-0 text-primary" />
                    <span className="text-[10.5px] leading-snug text-primary">
                      {corrigirTextoNota(p.sugestao)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* rodapé fixo — aponta o maior ganho */}
        <div className="flex h-[34px] flex-shrink-0 items-center gap-1.5 border-t border-border bg-muted/30 px-5 text-[10px] leading-snug text-muted-label">
          <Info className="h-3 w-3 flex-shrink-0" />
          <span className="min-w-0 truncate">
            {nota.competencia_fechada ? 'Competência fechada: esta nota não muda mais. ' : 'Competência atual: a nota acompanha o trabalho no CRM. '}
            {emAberto > 0 && maior ? (
              <>Os pilares em <b className="font-semibold text-primary">bordô</b> são os de maior ganho: fechar{' '}
                <b className="font-semibold text-muted-foreground">{corrigirTextoNota(maior.rotulo ?? maior.pilar)}</b> vale <b className="font-semibold text-muted-foreground">{maiorGap} {maiorGap === 1 ? 'ponto' : 'pontos'}</b>.</>
            ) : (
              <>Todos os pilares estão cheios — é manter.</>
            )}
          </span>
        </div>
      </DialogContent>

      <GuiaNotasDialog open={guiaAberto} onOpenChange={setGuiaAberto} tipo={nota.tipo} />
    </Dialog>
  );
}
